import {
  useCallback, useEffect, useRef, useState, type CSSProperties, type JSX, type ReactNode,
} from 'react'
import {
  api, ApiError, onepagerPath,
  type AccountCharacterization, type AccountDetail as AccountDetailPayload, type AccountEstimate,
  type ConfidenceLabel, type DetailAccount, type EndUse, type EstimateFigures,
  type EstimateFlag, type HourValue, type RecomputedPayload, type ScenarioPayload,
} from '../api.js'
import { CHROME_HEIGHT } from '../components/Chrome.js'
import { Derivation } from '../components/Derivation.js'
import { NoteFeed } from '../components/NoteFeed.js'
import { ProfileChart } from '../components/ProfileChart.js'
import { endUseWords, StrategyList } from '../components/StrategyList.js'
import {
  CharacterizationLabel, ConfidencePill, formatDate, formatHour, formatKw, formatMoney, grouped,
  KwFigure, StatusPill, type Characterization,
} from '../components/Pill.js'
import { scenarioSummary, WhatIf } from '../components/WhatIf.js'
import { T } from '../tokens.js'
import { Presentation } from './Presentation.js'

/**
 * The account drill-down — the signature surface (F-11, spec §7.2).
 *
 * ## What this screen is, and what it deliberately is not
 *
 * **It computes nothing.** Every figure below arrives from
 * `GET /api/accounts/:id` already computed, including the *event window* the
 * chart draws its band from. That last one is the whole reason this task
 * touched the server: the window is program configuration, and configuration
 * hard-coded in a browser is configuration nobody can version, audit or change
 * without a release. The mock puts the confidence weights there; this package
 * puts nothing there.
 *
 * **The note surface is `NoteFeed`, and this screen owns none of it.** Plan 4
 * rendered the pinned-or-most-recent note read-only here, deliberately, because
 * Plan 5 (F-17) owns capture, attribution, tagging, redaction and history. That
 * card is gone: `components/NoteFeed.tsx` reads its own payload from
 * `GET /api/accounts/:id/notes`, which is the record — redacted rows included,
 * marked — while `payload.note` is the *summary*, which excludes them. This
 * screen passes it an account id and the characterization state and reads
 * nothing back.
 *
 * **It renders every flag, not `flags[0]`.** The mock renders the first. An
 * account carrying two flags and showing one is a rep making a commitment
 * against information the system already had — `acct-fill-0052` carries two on
 * the seeded book, and the test uses it for exactly that reason.
 *
 * ## The three display rules this screen is the enforceable form of
 *
 * *Rule 1 — no bare kW figure.* Every kW quantity here goes through `KwFigure`,
 * whose required `confidence` makes the violation a type error. The ones in the
 * right rail and the decomposition use `label="adjacent"` pointing at the
 * header's confidence pill, so the label is named rather than merely nearby.
 *
 * *Rule 4 — absence never looks like deficiency.* An uncharacterized account
 * gets grey body text and nothing else: no amber, no icon, no "add
 * characterization" affordance. 212 of the 313 accounts on this book are
 * uncharacterized and always will be. An account with no note is the same.
 *
 * *Rule 6 — provenance on human-entered data.* The characterization card
 * carries who recorded it, when, and from what source, or it carries nothing at
 * all — because for an uncharacterized account there is no human entry to
 * attribute.
 *
 * ## Two places this screen departs from the plan's text, on purpose
 *
 * **The breadcrumb says `My week`, not `My week — West Valley book`.** Task 12
 * already dropped "West Valley" from the targeting list's own title, and its
 * reasoning holds harder in a breadcrumb: there is no territory in this data
 * model, so the phrase is a claim the system cannot support, and a breadcrumb
 * that names a destination differently from the destination's own heading is
 * wrong twice. Recorded in task-15-report.md rather than fixed silently.
 *
 * **A hand-entered nomination does not "bind" at any hour.** Task 14 withheld
 * `BINDS` from the chart when `nomination.overridden` is true and left the
 * screen-level half open (its finding F-5). This is that half: the KPI caption
 * says the figure was entered by hand and reports the weakest hour as an
 * observation rather than as a cause. Task 17's what-if is where an `overridden`
 * estimate is first *produced*, so both behaviours are now exercised rather than
 * merely implemented.
 *
 * ## The scenario state is the page, not a badge (R-18, spec §7.4)
 *
 * When a what-if is on screen this becomes a different-looking page: a 6px red
 * bar above the brand rule (`Chrome.tsx`), a **sticky** band naming the scenario
 * and offering the one revert, a shifted page background, a red chart series,
 * and every figure below drawn from the scenario in red. A rep who screenshots
 * the middle of the page and sends it to a colleague sends something that says
 * what it is; a badge in a corner does not survive that crop, and this is the
 * screen whose numbers get quoted to customers.
 *
 * **The figures switch; the confidence label does not.** `record.confidence` is
 * what every label on this page names, under a scenario as well as of record.
 * A scenario is scored fresh and never goes through the cohort pass that writes
 * `estimates.confidence`, so its own label is pre-cohort and would show all 26
 * Medium accounts on the book "improving" the moment a rep opened a what-if.
 * `WhatIf.tsx` carries the measurement and says so in words on the panel.
 *
 * **Revert is one action against state this screen already holds.** The record
 * payload is never replaced — the scenario lives beside it — so reverting is
 * `setScenario(null)` and cannot land on a different run the way a re-fetch
 * could.
 */

/**
 * The unbuilt controls, said the way `Chrome.tsx` says it about its three
 * unbuilt tabs: rendered, disabled, and honest about why. A control that leads
 * nowhere silently is indistinguishable — to a rep, on a Tuesday — from a tool
 * whose buttons are broken.
 */
const NOT_BUILT_OUTCOMES = 'Not built yet — outcome capture arrives in a later release.'

/**
 * The banner's own promise, and it has to be true.
 *
 * It is: `toPresentationPayload` builds the customer payload by naming the
 * fields that go into it and names no flag field anywhere, so
 * `?mode=presentation` and the one-pager — which is rendered from that same
 * projection — carry neither the codes nor the messages. Both directions are
 * pinned server-side (`presentation.test.ts`, `onepager.test.ts`) against an
 * account that actually has flags, which is the only way that assertion means
 * anything.
 */
const FLAGS_HIDDEN_NOTE = 'Hidden in presentation view and on the one-pager.'

/**
 * Said out loud under a scenario (I-1).
 *
 * The same sentence the confidence panel already uses, for the same reason: a
 * scenario is scored pre-cohort, so it cannot produce `cohort_outlier` — the
 * flag whose message is "verify before nominating", and which is precisely what
 * explains a stepped-down confidence label. The page keeps the record's flags
 * rather than dropping them, and this says whose they are so a rep does not
 * read them as computed from the scenario's own figures.
 */
const FLAGS_OF_RECORD_NOTE = 'These are the estimate of record’s flags — a scenario '
  + 'is scored before the cohort comparison, so it has none of its own.'

const BASELINE_CAPTION = 'Peak of the 10-in-10 comparable-day baseline'

/** Display rule 2, in the words the mock uses, on the band that cannot scroll away. */
const SCENARIO_PILL = 'Scenario — not the estimate of record'
const REVERT_LABEL = 'Revert to estimate of record'

/**
 * A what-if is a question about a figure, and this run produced none for an
 * account it did not score. The scenario route agrees, answering
 * `estimate_not_found` — so the control is disabled and says which of the two
 * kinds of "not available" this is.
 */
const NO_ESTIMATE_TO_ASK_ABOUT =
  'No estimate of record on this run, so there is nothing to ask a what-if against.'

/** The same fact, said about the other control. */
const NO_ESTIMATE_TO_PRESENT =
  'No estimate of record on this run, so there is nothing to present.'

/**
 * **A scenario has no presentation view, and this is the whole reason.**
 *
 * Presentation mode is a *different payload* the server builds (R-28), and
 * there is no such payload for a figure that is not the estimate of record:
 * `PresentationEstimate` has no field that says "not of record", which is
 * exactly why Task 8 made `?asOf` combined with `?mode=presentation` a 400
 * rather than serving a different day's figure to a customer unlabelled. A
 * scenario is the same shape of problem and one worse — its confidence label is
 * **pre-cohort** and not comparable with the record's, and a customer screen
 * has nowhere to explain the discrepancy.
 *
 * Building the screen from the scenario envelope this page already holds would
 * "work", and would be the precise failure R-28 names: a customer-facing screen
 * rendered by filtering an internal payload in the browser, identical on screen
 * and hollow underneath.
 *
 * So the control is refused rather than quietly showing the record — a rep who
 * believes the customer is looking at the what-if they just ran, and is not, is
 * the worst outcome available here. The scenario's customer-facing artifact is
 * the one-pager, which is rendered server-side and carries the scenario band.
 */
const SCENARIO_HAS_NO_PRESENTATION =
  'Revert to the estimate of record to present. A what-if is not the estimate of record, and the '
  + 'customer-facing payload has no field that says so — the one-pager is the document that can '
  + 'carry a scenario.'

/**
 * **The as-of control, and the second thing on this page that is not the
 * estimate of record** (Task 8, R-22).
 *
 * The design brief calls the Sunrise Elementary July-versus-September contrast
 * the whole reason occupancy calendars exist, and the mock carries it only as
 * prose. `GET /api/accounts/:id?asOf=` scores the same account for another day
 * against the record's *own* stamped configuration and characterization
 * revision, so the only thing that differs between the two figures is the day.
 *
 * **Its band is not the scenario's band, and that is a requirement rather than
 * a decoration.** Two distinct states sharing one visual treatment are one
 * state as far as the reader is concerned. A scenario is red — red bar, red
 * band, red series, red figures, a red page. A recompute is the `stale` amber:
 * a different pill, a different tint, `warnInk` figures, and a different
 * sentence. The chart's series is deliberately left in the record's colours,
 * because red is the scenario's and lending it to a second state is exactly the
 * collapse this treatment exists to prevent.
 */
const AS_OF_LABEL = 'As of'
const AS_OF_PILL = 'Recomputed for another date — not the estimate of record'
const AS_OF_REVERT = 'Back to the estimate of record'

/**
 * The same trap Task 17 met on the scenario panel, in the same words about a
 * different mechanism: `estimates.confidence` on the record is written *after*
 * a run, by the cohort pass. A recompute belongs to no run — measured,
 * `acct-fill-0035` is `medium` of record and `high` recomputed at the run's own
 * as-of date with nothing else changed — so its label is the engine's
 * pre-cohort one and the whole page keeps naming the record's.
 */
const AS_OF_CONFIDENCE_NOTE =
  'Confidence stays the estimate of record’s. A recompute belongs to no run and never goes '
  + 'through the cohort comparison that sets the label, so a label computed here would not be '
  + 'comparable with the one above.'

/** What a failed recompute says when the server sent no message of its own. */
const RECOMPUTE_FAILED = 'That date could not be scored. Choose another date, or go back to the '
  + 'estimate of record.'

/**
 * **A scenario is pinned to the run's own as-of date, so the two controls
 * cannot both be open.**
 *
 * `POST /api/accounts/:id/scenarios` takes overrides and no date: it recomputes
 * at `recordEstimateId`'s as-of date under the record's stamped configuration.
 * There is no request that means "this what-if, in September", so offering the
 * date control under a scenario would offer a combination nothing can answer —
 * and answering it by quietly dropping one of the two would be the §7.3 failure
 * inverted, a rep looking at a figure no control on the page selects.
 */
const SCENARIO_HAS_NO_AS_OF =
  'Revert to the estimate of record to move the date. A what-if is computed against the run’s '
  + 'own as-of date, and there is no request that carries both.'

/** The same fact said from the other side, on the what-if control. */
const AS_OF_HAS_NO_WHATIF =
  'Go back to the estimate of record to ask a what-if. A what-if is computed against the run’s '
  + 'own as-of date, and there is no request that carries both.'

/**
 * `?asOf` with `?mode=presentation` is a **400** (Task 8), because
 * `PresentationEstimate` has no field that says "not the estimate of record"
 * and serving a different day's figure to a customer unlabelled is display rule
 * 2 broken on the one surface a customer is looking at.
 */
const AS_OF_HAS_NO_PRESENTATION =
  'Go back to the estimate of record to present. The customer-facing payload has no field that '
  + 'says a figure was computed for another date, so this route refuses the combination rather '
  + 'than showing a customer a figure the program does not stand behind.'

/**
 * **The one-pager takes `?scenario=` and does not take `?asOf=`** — its
 * recognised parameters are `scenario` and `generatedOn` and nothing else.
 *
 * So a rep with a September figure on screen who opened the document would get
 * the *July* estimate of record on paper, silently. That is precisely the F-3
 * hole this task closed for scenarios, in a second key, and the answer is the
 * same one: refuse loudly rather than export a figure that disagrees with the
 * screen. Closing it properly is a route change, not a link change — see the
 * report's findings.
 */
const AS_OF_HAS_NO_ONEPAGER =
  'Go back to the estimate of record to print. The one-pager carries a scenario and does not '
  + 'carry a date — it would print the estimate of record while this page shows another day.'

const microLabel: CSSProperties = {
  fontSize: T.micro.size,
  fontWeight: T.weight.semi,
  letterSpacing: T.micro.tracking,
  textTransform: T.micro.transform,
  color: T.color.micro,
}

const sectionRule: CSSProperties = {
  borderTop: `1.5px solid ${T.color.rule}`,
  paddingTop: 20,
  marginTop: 24,
}

type Load =
  | { phase: 'loading' }
  | { phase: 'failed'; message: string }
  | { phase: 'ready'; payload: AccountDetailPayload }

/**
 * The as-of recompute, as a phase rather than a nullable payload.
 *
 * `loading` and `failed` both have to put the band on screen: the moment a rep
 * changes the date the page is no longer answering the question the header
 * says it is, and a page that showed the record's figures under an unchanged
 * chrome while a request for another day was in flight would be quietly wrong
 * for as long as the request took. So the band appears with the date, and the
 * *figures* switch only on `ready`.
 */
type Recompute =
  | { phase: 'none' }
  | { phase: 'loading'; asOf: string }
  | { phase: 'ready'; asOf: string; payload: RecomputedPayload }
  | { phase: 'failed'; asOf: string; message: string }

const NOT_FOUND = 'That account is not in this book, or it has been removed.'
const NO_RUN = 'No scoring run has completed yet, so there is nothing to show for this account.'

function failureMessage(error: ApiError): string {
  if (error.code === 'account_not_found') return NOT_FOUND
  if (error.code === 'no_completed_run') return NO_RUN
  return error.message
}

/**
 * `profile` and `contextLoad` stay two separate fields all the way out to this
 * screen — merging them any earlier would let a curtailable-kW figure and a
 * plain meter average travel under one name, which is exactly the confusion
 * R-2 exists to prevent. `<ProfileChart>` already dashes anything outside
 * `eventWindow` (Task 14), so a single hour-sorted array is all it needs; this
 * is the one place that array gets built.
 */
function chartProfile(profile: HourValue[], contextLoad: HourValue[]): HourValue[] {
  return [...profile, ...contextLoad].sort((a, b) => a.hour - b.hour)
}

export interface AccountDetailProps {
  accountId: string
  /** Back to the targeting list. The breadcrumb and the rail's action stack share it. */
  onBack: () => void
  /**
   * Told the active scenario's name, or `null`.
   *
   * The app bar is `Chrome.tsx`'s and lives above this screen, and R-18 asks
   * for the scenario in the layout rather than in one panel: a 6px red bar over
   * the brand rule and the scenario's name beside the wordmark. This is the one
   * fact that has to travel up. Optional so the screen renders standalone.
   */
  onScenario?: (name: string | null) => void
}

export function AccountDetail({ accountId, onBack, onScenario }: AccountDetailProps): JSX.Element {
  const [load, setLoad] = useState<Load>({ phase: 'loading' })
  /**
   * The what-if, beside the record rather than in place of it.
   *
   * **This is what makes revert one action.** The record payload in `load` is
   * never overwritten, so returning to it is dropping this and not a second
   * `GET` that could be served by a newer run — a scenario is pinned to
   * `recordEstimateId`, and reverting onto a different estimate would quietly
   * end the comparison it was a comparison against.
   */
  const [scenario, setScenario] = useState<ScenarioPayload | null>(null)
  /**
   * How many times the rep has reverted. It is the what-if panel's `key`.
   *
   * Revert has to put the *controls* back as well as the figures, and the panel
   * owns its own draft. Remounting it is one line and cannot drift; an
   * `onRevert` the panel subscribed to would be a second path to the same
   * state, and the one that eventually disagrees with the other. It is a
   * counter rather than "is there a scenario" because the latter would also
   * fire when a scenario *arrives*, wiping the controls that produced it.
   */
  const [reverts, setReverts] = useState(0)
  /**
   * Whether the laptop has been turned around (R-28).
   *
   * It is a boolean and not a payload: `Presentation` fetches its **own**
   * customer-facing payload from `?mode=presentation` and is handed nothing
   * from this screen's state. Passing the drill-down payload down into it —
   * even a subset of it — is the client-side filtering the requirement exists
   * to refuse, and it would look identical on screen.
   */
  const [presenting, setPresenting] = useState(false)
  /**
   * Where the keyboard goes back to when presentation mode closes.
   *
   * It has to be captured in the *handler*, not in an effect: the drill-down
   * goes under `display: none` in the same commit that mounts the overlay, and
   * a browser blurs an element that is display-noned, so by the time any effect
   * runs `document.activeElement` is already `<body>`. Restoring is the
   * mirror-image problem — the wrapper is still hidden while `onExit` runs, and
   * focusing a hidden element does nothing — so it happens in the effect below,
   * after the commit that makes the page visible again.
   *
   * Without this, Escape out of a presentation drops a keyboard user at the top
   * of the document and they tab the whole page again to get back to the
   * control they left from.
   */
  const returnFocus = useRef<HTMLElement | null>(null)
  useEffect(() => {
    if (presenting) return
    const element = returnFocus.current
    returnFocus.current = null
    element?.focus()
  }, [presenting])
  /**
   * The estimate recomputed for another day, beside the record rather than in
   * place of it — the same shape of state as `scenario` and for the same
   * reason. Going back to the record is dropping this, not a second `GET` that
   * a newer run could answer differently.
   */
  const [recompute, setRecompute] = useState<Recompute>({ phase: 'none' })
  /**
   * Which recompute is the current question. A rep who moves the date twice has
   * two requests in flight and the slower one is not the answer to the date the
   * control is showing — the same guard `WhatIf.tsx` puts on its scenario
   * requests, and for the same reason.
   */
  const era = useRef(0)

  useEffect(() => {
    let cancelled = false
    setLoad({ phase: 'loading' })
    setScenario(null)
    // Navigating to another account leaves presentation mode. A customer screen
    // that survived a change of account would be showing one customer's figures
    // under another customer's name for as long as the fetch took.
    setPresenting(false)
    // And it goes back to the estimate of record. A date is a question about
    // one account; carrying it across would put the next account's header date
    // on a figure nobody asked for, and bumping the era makes an answer already
    // in flight unable to land.
    era.current += 1
    setRecompute({ phase: 'none' })
    void api.account(accountId)
      .then((payload) => { if (!cancelled) setLoad({ phase: 'ready', payload }) })
      .catch((error: unknown) => {
        if (cancelled) return
        setLoad({
          phase: 'failed',
          message: error instanceof ApiError
            ? failureMessage(error)
            : 'Something went wrong loading this account.',
        })
      })
    return () => { cancelled = true }
  }, [accountId])

  /**
   * Ask the server for another day. `date` equal to the record's own as-of date
   * is the record, not a recompute of it — the route would happily answer, and
   * the answer would wear the band while saying nothing new.
   */
  const onAsOf = useCallback((date: string, recordAsOf: string): void => {
    if (date === recordAsOf) {
      era.current += 1
      setRecompute({ phase: 'none' })
      return
    }
    const mine = era.current + 1
    era.current = mine
    setRecompute({ phase: 'loading', asOf: date })
    void api.recompute(accountId, date)
      .then((payload) => {
        if (era.current === mine) setRecompute({ phase: 'ready', asOf: date, payload })
      })
      .catch((error: unknown) => {
        if (era.current !== mine) return
        setRecompute({
          phase: 'failed',
          asOf: date,
          // The route's own message when it sent one. `asOf: 2026-11-01 is
          // outside the 2026-06-01 to 2026-10-15 program season` names the
          // bounds from the stamped configuration, which is the only place
          // this package can honestly learn them from.
          message: error instanceof ApiError ? error.detail ?? error.message : RECOMPUTE_FAILED,
        })
      })
  }, [accountId])

  const onRecordDate = useCallback((): void => {
    era.current += 1
    setRecompute({ phase: 'none' })
  }, [])

  const scenarioName = scenario === null ? null : scenario.scenario.name

  useEffect(() => { onScenario?.(scenarioName) }, [onScenario, scenarioName])
  // Separate from the effect above, and only for the unmount: a cleanup on the
  // first would run on every change and blank the app bar for a frame between
  // one scenario and the next. Navigating back to the list unmounts this
  // screen, and the red bar must not outlive it.
  useEffect(() => () => { onScenario?.(null) }, [onScenario])

  const name = load.phase === 'ready' ? load.payload.account.name : accountId

  return (
    <div
      data-scenario-page={scenario === null ? 'false' : 'true'}
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        // The shifted page background of spec §7.4. It is the reason the middle
        // of a scrolled page still reads as a scenario even where no figure and
        // no band happens to be in the crop.
        background: scenario !== null
          ? T.tint.red
          : recompute.phase === 'none' ? '#fff' : T.tint.stale,
      }}
      data-as-of-page={recompute.phase === 'none' ? 'false' : 'true'}
    >
      {/*
        **Display rule 7, and the reason this wrapper exists at all.**

        `<Presentation>` below is `position: fixed`, so while it is open the
        *visible* screen is already correct. That is not the requirement. Rule 7
        is "note content never appears in any customer-facing artifact or mode",
        and a printed page is an artifact: an overlay that merely covers the
        drill-down leaves the note body, "verify before nominating", the cohort
        percentile and the fit diagnostics in `Page.printToPDF`'s output, in
        `innerText`, in the accessibility tree, in find-in-page and in a
        select-all. Measured — Cmd+P from presentation mode produced a two-page
        customer document carrying all of them.

        `display: none` removes the content from every one of those paths in one
        change, and unlike `inert` (which jsdom does not implement) it is
        assertable, so the test that pins it is a real one.

        `display: contents` rather than `block` in the ordinary case, so this
        element adds nothing to the flex column it sits in — the layout with the
        wrapper is the layout without it. The subtree is left *mounted* on
        purpose: leaving presentation mode must not re-fetch the account or
        throw away an open derivation row (display rule 5's cache), which is
        what unmounting would do.
      */}
      <div
        data-testid="drilldown-under-presentation"
        style={{ display: presenting ? 'none' : 'contents' }}
      >
        <Breadcrumb name={name} onBack={onBack} />
        {scenario === null || load.phase !== 'ready' || load.payload.estimate === null
          ? null
          : (
            <ScenarioBand
              scenario={scenario}
              record={load.payload.estimate}
              onRevert={() => { setScenario(null); setReverts((count) => count + 1) }}
            />
          )}
        {recompute.phase === 'none' || load.phase !== 'ready' || load.payload.estimate === null
          ? null
          : (
            <AsOfBand
              state={recompute}
              record={load.payload.estimate}
              onRevert={onRecordDate}
            />
          )}
        {load.phase === 'loading'
          ? <p style={{ margin: 22, fontSize: 13.5, color: T.color.body }}>Loading this account…</p>
          : null}
        {load.phase === 'failed'
          ? <p style={{ margin: 22, fontSize: 13.5, color: T.color.body, maxWidth: '60ch' }}>{load.message}</p>
          : null}
        {load.phase === 'ready'
          ? (
            <Loaded
              payload={load.payload}
              onBack={onBack}
              scenario={scenario}
              onScenarioChange={setScenario}
              reverts={reverts}
              onPresent={() => {
                const active = document.activeElement
                returnFocus.current = active instanceof HTMLElement ? active : null
                setPresenting(true)
              }}
              recompute={recompute}
              onAsOf={onAsOf}
              onRecordDate={onRecordDate}
            />
          )
          : null}
      </div>
      {presenting
        ? <Presentation accountId={accountId} onExit={() => { setPresenting(false) }} />
        : null}
    </div>
  )
}

/**
 * The band, and it is `position: sticky` on purpose.
 *
 * Spec §7.4 asks for a persistent band across the top of the viewport, and the
 * mock's is a static strip below the breadcrumb that scrolls away after about
 * one screen — which is exactly the height at which a rep is looking at the
 * chart and the KPI figures they are about to read aloud. Sticky is the
 * difference between "the page said so at the top" and "the page says so".
 *
 * The revert control lives here rather than in the panel because it must be
 * reachable from wherever the rep has scrolled to, and because a scenario can
 * be on the page with the panel closed.
 */
function ScenarioBand(
  { scenario, record, onRevert }:
  { scenario: ScenarioPayload; record: EstimateFigures; onRevert: () => void },
): JSX.Element {
  return (
    <div
      data-testid="scenario-band"
      data-scenario-name={scenario.scenario.name}
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 20,
        background: T.tint.red,
        borderTop: `2px solid ${T.color.red}`,
        borderBottom: `2px solid ${T.color.red}`,
        padding: '12px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        <span
          data-testid="scenario-pill"
          style={{
            background: T.color.red,
            color: '#fff',
            padding: '5px 13px',
            borderRadius: T.radius.pill,
            fontSize: 10.5,
            fontWeight: T.weight.bold,
            letterSpacing: '0.09em',
            textTransform: T.micro.transform,
            whiteSpace: 'nowrap',
          }}
        >
          {SCENARIO_PILL}
        </span>
        <span data-testid="scenario-summary" style={{ fontSize: 13 }}>
          {scenarioSummary(scenario, record)}
        </span>
      </div>
      <button
        type="button"
        onClick={onRevert}
        data-testid="revert"
        style={{
          border: `1.5px solid ${T.color.red}`,
          background: T.color.red,
          color: '#fff',
          padding: '10px 14px',
          fontFamily: T.fontFamily,
          fontSize: T.micro.size,
          fontWeight: T.weight.bold,
          letterSpacing: '0.08em',
          textTransform: T.micro.transform,
          borderRadius: T.radius.button,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        {REVERT_LABEL}
      </button>
    </div>
  )
}

/**
 * What day this is, what it says, and what it is not.
 *
 * **Both operands are printed** — the recomputed nomination and the record's,
 * each with its own date. Task 17 made that an assertion rather than a habit
 * for the scenario delta and it holds harder here: the whole point of the
 * control is a comparison, and a comparison with one number in it is a number.
 * Sunrise Elementary reads `24 Sep 2026 · nomination 51.5 kW against 11.2 kW of
 * record as of 15 Jul 2026`, which is the brief's 4.6x said in one line.
 */
export function asOfSummary(payload: RecomputedPayload, record: EstimateFigures): string {
  return `${formatDate(payload.estimate.asOfDate)} · nomination `
    + `${formatKw(payload.estimate.result.nomination.kw)} against `
    + `${formatKw(record.result.nomination.kw)} of record as of `
    + `${formatDate(payload.recompute.recordAsOfDate)}`
}

/**
 * The as-of band — **sticky, like the scenario band, and amber, unlike it**.
 *
 * Sticky for the reason spec §7.4 gives about scenarios: a rep reading the
 * chart is a screen below the header, and "the page said so at the top" is not
 * the same claim as "the page says so". Amber because it is a *different*
 * state: `data-band` names which of the two this is, the pill is a different
 * sentence, the tint is `stale` rather than `red`, and the one action back is
 * worded for a date rather than for a question.
 *
 * The three phases are all a band. A recompute in flight is already not the
 * estimate of record — the header's date says so — and a failed one is the only
 * place the server's own refusal (`asOf: 2026-11-01 is outside the 2026-06-01
 * to 2026-10-15 program season`) can be read.
 */
function AsOfBand(
  { state, record, onRevert }:
  { state: Recompute; record: EstimateFigures; onRevert: () => void },
): JSX.Element | null {
  if (state.phase === 'none') return null
  return (
    <div
      data-testid="as-of-band"
      data-band="as-of"
      data-as-of={state.asOf}
      data-phase={state.phase}
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 20,
        background: T.tint.stale,
        borderTop: `2px solid ${T.color.amber}`,
        borderBottom: `2px solid ${T.color.amber}`,
        padding: '12px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <span
            data-testid="as-of-pill"
            style={{
              background: T.color.amber,
              color: T.color.onAmber,
              padding: '5px 13px',
              borderRadius: T.radius.pill,
              fontSize: 10.5,
              fontWeight: T.weight.bold,
              letterSpacing: '0.09em',
              textTransform: T.micro.transform,
              whiteSpace: 'nowrap',
            }}
          >
            {AS_OF_PILL}
          </span>
          <span data-testid="as-of-summary" style={{ fontSize: 13, color: T.color.warnInk }}>
            {state.phase === 'ready'
              ? asOfSummary(state.payload, record)
              : state.phase === 'loading'
                ? `Scoring this account as of ${formatDate(state.asOf)}…`
                : state.message}
          </span>
        </div>
        <div
          data-testid="as-of-confidence-note"
          style={{ fontSize: 11.5, color: T.color.warnInk, marginTop: 5, maxWidth: '86ch' }}
        >
          {AS_OF_CONFIDENCE_NOTE}
        </div>
      </div>
      <button
        type="button"
        onClick={onRevert}
        data-testid="as-of-revert"
        style={{
          border: `1.5px solid ${T.color.warnInk}`,
          background: T.color.warnInk,
          color: '#fff',
          padding: '10px 14px',
          fontFamily: T.fontFamily,
          fontSize: T.micro.size,
          fontWeight: T.weight.bold,
          letterSpacing: '0.08em',
          textTransform: T.micro.transform,
          borderRadius: T.radius.button,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        {AS_OF_REVERT}
      </button>
    </div>
  )
}

/**
 * `Targeting / My week / {account name}`.
 *
 * The first two are real buttons rather than decoration — a breadcrumb whose
 * ancestors do not navigate is a picture of navigation. The last is the current
 * page and carries `aria-current`, so a screen reader is told which crumb is
 * where you are rather than being left to infer it from the styling.
 */
function Breadcrumb({ name, onBack }: { name: string; onBack: () => void }): JSX.Element {
  const crumb: CSSProperties = {
    ...microLabel,
    background: 'none',
    border: 0,
    padding: 0,
    fontFamily: T.fontFamily,
    cursor: 'pointer',
    color: T.color.muted,
  }
  return (
    <nav
      aria-label="Breadcrumb"
      data-testid="breadcrumb"
      style={{
        background: '#fff',
        borderBottom: `1.5px solid ${T.color.rule}`,
        padding: '12px 24px',
      }}
    >
      <ol style={{ display: 'flex', gap: 8, listStyle: 'none', margin: 0, padding: 0, alignItems: 'center' }}>
        <li><button type="button" onClick={onBack} style={crumb}>Targeting</button></li>
        <li aria-hidden="true" style={{ ...microLabel, color: T.color.chipOff }}>/</li>
        <li><button type="button" onClick={onBack} style={crumb}>My week</button></li>
        <li aria-hidden="true" style={{ ...microLabel, color: T.color.chipOff }}>/</li>
        <li aria-current="page" style={{ ...microLabel, color: T.color.ink }}>{name}</li>
      </ol>
    </nav>
  )
}

/**
 * The id of the element carrying this screen's confidence label.
 *
 * `KwFigure`'s `label="adjacent"` mode *requires* it: display rule 1 says a kW
 * figure never appears without its confidence, and "the label is up in the
 * header" has to name the element it means so that a screen reader on the
 * number hears the label too. One id for the screen, because there is one
 * estimate and therefore one label.
 */
const CONFIDENCE_ID = 'account-confidence'

interface LoadedProps {
  payload: AccountDetailPayload
  onBack: () => void
  scenario: ScenarioPayload | null
  onScenarioChange: (scenario: ScenarioPayload | null) => void
  /** The what-if panel's `key` — see `reverts` in `AccountDetail`. */
  reverts: number
  onPresent: () => void
  recompute: Recompute
  onAsOf: (date: string, recordAsOf: string) => void
  onRecordDate: () => void
}

function Loaded(
  {
    payload, onBack, scenario, onScenarioChange, reverts, onPresent,
    recompute, onAsOf, onRecordDate,
  }: LoadedProps,
): JSX.Element {
  /**
   * `payload.note` is deliberately not read.
   *
   * It is the drill-down summary's note — one row, the pinned or most recent,
   * and never a redacted one. The feed below is the *record* and reads
   * `GET /api/accounts/:id/notes` for itself, which serves every row including
   * the redacted ones, marked. Drawing the summary note *and* the feed would
   * put the same note on the page twice, from two queries that disagree about
   * redaction on purpose. The field stays on the wire and on the type because
   * the route still serves it and the targeting list (Task 11) is its other
   * reader.
   */
  const { account, estimate, characterization, rescoredSinceRun, rescoredCharacterization } = payload
  const [panelOpen, setPanelOpen] = useState(false)

  /**
   * **R-33's rejection offer, and the one decision it forces.**
   *
   * A rejected strategy offers the end-use exclusion it implies; it never
   * applies it. Where that offer *goes* depends on whether the rep already has
   * a what-if in flight, and the two answers are deliberately different:
   *
   * - **Panel closed** — `seed`. It opens the panel with the end use ticked and
   *   no request fired. There is no draft to lose, so there is nothing to ask
   *   about; the rep sees the record's own figures with a box ticked and
   *   applies it from there.
   * - **Panel open** — `pendingOffer`, and the open draft is not touched at
   *   all. A rep who has "5 hr" and a hand-entered nomination on screen is
   *   mid-conversation, and reseeding the panel would throw both away over a
   *   click they made about a different strategy. The offer waits as a one-line
   *   strip with one button, and accepting it *adds* to the draft they already
   *   have (`accepted`, which `WhatIf` merges rather than replaces).
   *
   * The seed is cleared as soon as the panel has taken it, so that the revert
   * remount — `key={reverts}` — does not silently re-tick a box the rep just
   * cleared.
   */
  const [seed, setSeed] = useState<readonly EndUse[] | null>(null)
  const [pendingOffer, setPendingOffer] = useState<readonly EndUse[] | null>(null)
  const [accepted, setAccepted] = useState<{ token: number; endUses: readonly EndUse[] } | null>(null)

  /**
   * Which figures the page is drawing, and which label it is naming.
   *
   * They come from different places on purpose. `shown` is the scenario's
   * estimate when there is one, so the KPI figures, the chart, the flags, the
   * decomposition and the rail all move together — a page showing 227.2 kW at
   * the top and 179.6 kW in the panel would be two answers to one question.
   * The **label** stays the record's in every case; see the module docblock and
   * `WhatIf.tsx` on `confidenceBasis`.
   */
  const recomputed = recompute.phase === 'ready' ? recompute.payload.estimate : null
  /**
   * A recompute is on the page at all — in flight, answered or refused. It is
   * what disables the three controls that cannot mean anything under it, and it
   * is deliberately not `recomputed !== null`: a rep must not be able to open a
   * what-if in the gap while a September request is in flight.
   */
  const asOfActive = recompute.phase !== 'none'

  const shown: EstimateFigures | null = recomputed
    ?? (scenario === null ? estimate : scenario.estimate)
  const isScenario = scenario !== null
  /**
   * Which of the three states the figures are in. Three states, three
   * treatments — the same rule display rule 3 states about characterization,
   * applied to the reason a figure is on the page.
   */
  const figures: FigureState = isScenario ? 'scenario' : recomputed === null ? 'record' : 'asOf'

  /**
   * The one-pager's URL, **carrying the scenario** (F-3, R-18, display rule 2).
   *
   * The overrides are the route's echoed ones rather than the panel's draft, so
   * the document describes what the server actually applied. `null` refuses the
   * link outright under a recompute, because the route has no `?asOf` and the
   * document would print the estimate of record while this page shows another
   * day — the same silent swap this task exists to close, in a second key.
   */
  const onepager: string | null = asOfActive
    ? null
    : onepagerPath(account.id, scenario === null ? null : scenario.scenario.overrides)

  const onScenario = useCallback((next: ScenarioPayload | null) => {
    onScenarioChange(next)
  }, [onScenarioChange])

  /**
   * The panel has mounted and taken the seed, so the seed is spent.
   *
   * Without this the revert — which remounts `WhatIf` on a new `key` precisely
   * to put the controls back at the record's own configuration — would hand it
   * the same `initialExcluded` again and re-tick the box the rep just reverted
   * away from, with no scenario on the page to explain it.
   */
  useEffect(() => {
    if (panelOpen && seed !== null) setSeed(null)
  }, [panelOpen, seed])

  /**
   * Where a rejection's suggested exclusion goes. See `seed` above.
   *
   * `undefined` — no offer control at all — whenever the what-if cannot be
   * opened: an account this run did not score has no estimate to hang a
   * `strategy_presentations` row on (the route answers 404), and an as-of
   * recompute on the page disables the panel outright. Neither is a case where
   * a "Not available" button could do anything, and a control that does nothing
   * is the failure this whole interaction is written to avoid.
   */
  const onSuggestExclusion = useCallback((endUses: EndUse[]): void => {
    if (panelOpen) {
      setPendingOffer(endUses)
      return
    }
    setSeed(endUses)
    setPanelOpen(true)
  }, [panelOpen])

  const acceptOffer = useCallback((): void => {
    if (pendingOffer === null) return
    setAccepted((previous) => ({ token: (previous?.token ?? 0) + 1, endUses: pendingOffer }))
    setPendingOffer(null)
  }, [pendingOffer])

  const togglePanel = useCallback((): void => {
    // Closing drops both halves of any outstanding offer. The offer is about a
    // panel, and a rep who closed the panel has answered the question. Written
    // against `panelOpen` rather than inside a `setPanelOpen` updater: an
    // updater must be pure, and this one would have two `setState` calls in it.
    if (panelOpen) {
      setSeed(null)
      setPendingOffer(null)
    }
    setPanelOpen(!panelOpen)
  }, [panelOpen])

  return (
    <>
      {estimate === null || !panelOpen || asOfActive
        ? null
        : (
          <WhatIf
            /*
              Remounted by the revert, which is what puts the controls back at
              the record's own configuration in the same action that drops the
              scenario. Keyed on the *count* rather than on "is there a
              scenario": the second form also fires when a scenario arrives,
              which would clear the very checkbox that produced it. Found by a
              test, not by reading it.
            */
            key={reverts}
            accountId={account.id}
            record={estimate}
            scenario={scenario}
            onScenario={onScenario}
            confidenceLabelId={CONFIDENCE_ID}
            initialExcluded={seed ?? undefined}
            addExclusions={accepted ?? undefined}
          />
        )}
      {pendingOffer === null || estimate === null || !panelOpen || asOfActive
        ? null
        : (
          /*
            The offer against an OPEN panel. It states what is on the table and
            leaves the draft alone until the rep says so — the accept is the
            rep applying the exclusion through the scenario path, which is the
            deliberate act R-33 asks for. Nothing here recomputes anything; the
            button hands the end uses to `WhatIf`, which merges them into
            whatever the rep already has.
          */
          <div
            data-testid="exclusion-offer"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              flexWrap: 'wrap',
              background: T.tint.blue,
              borderBottom: `1.5px solid ${T.color.rule}`,
              padding: '10px 24px',
              fontSize: 13,
              color: T.color.body,
            }}
          >
            <span>
              {`A rejected strategy suggests withholding ${pendingOffer.map(endUseWords).join(' and ')}. `
                + 'Your open what-if has not been changed.'}
            </span>
            <button
              type="button"
              data-testid="accept-exclusion"
              onClick={acceptOffer}
              style={{
                border: `1.5px solid ${T.color.navy}`,
                background: '#fff',
                color: T.color.navy,
                borderRadius: T.radius.button,
                fontFamily: T.fontFamily,
                fontSize: 11,
                fontWeight: T.weight.bold,
                letterSpacing: '0.07em',
                textTransform: T.micro.transform,
                padding: '7px 12px',
                cursor: 'pointer',
              }}
            >
              Add to this what-if
            </button>
          </div>
        )}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `1fr ${T.rail.account}px`,
          alignItems: 'start',
          background: isScenario ? T.tint.red : asOfActive ? T.tint.stale : '#fff',
        }}
        data-testid="drilldown-grid"
      >
        <div style={{ padding: '22px 24px 48px', minWidth: 0 }}>
          <Header
            account={account}
            estimate={estimate}
            panelOpen={panelOpen}
            onTogglePanel={togglePanel}
            scenarioOpen={isScenario}
            onPresent={onPresent}
            recompute={recompute}
            onAsOf={onAsOf}
            onRecordDate={onRecordDate}
            onepager={onepager}
          />
          {estimate === null || shown === null
            ? <NotScored account={account} />
            : (
              <>
                {/*
                  **Under a scenario these are the record's flags (I-1).**

                  A scenario is scored *pre-cohort*, so `shown.result.flags` can
                  never contain `cohort_outlier` — which meant that opening a
                  what-if silently dropped the "verify before nominating"
                  warning on 31 accounts of the seeded book, **with every number
                  unchanged**. The page already keeps the record's *confidence
                  label* under a scenario for precisely that reason; dropping
                  the flag that explains that label, at the moment a rep is
                  doing arithmetic in front of a customer, is the same decision
                  made backwards. A scenario asks the same question on the same
                  day with one assumption changed, so the record's flags still
                  describe the account it is being asked about.

                  **A recompute is different and keeps its own**, which is why
                  this is not simply `estimate.result.flags`. `?asOf` moves the
                  *date*: Sunrise Elementary carries `unoccupied_calendar` in
                  July and does not in September, and printing the July flag
                  over the September figure would be a fact about a period the
                  page is no longer showing. The record's cohort standing is
                  likewise a fact about the run's own date. Both halves of that
                  contrast are meant to be on screen — see the as-of tests.
                */}
                <InternalFlags
                  flags={isScenario ? estimate.result.flags : shown.result.flags}
                  basis={isScenario ? 'record-under-scenario' : figures === 'asOf' ? 'recomputed' : 'record'}
                />
                <section style={sectionRule}>
                  <ProfileChart
                    profile={chartProfile(shown.result.profile, shown.result.contextLoad)}
                    nomination={{
                      kw: shown.result.nomination.kw,
                      bindingHour: shown.result.nomination.bindingHour,
                      spanStartHour: shown.result.nomination.spanStartHour,
                      spanEndHour: shown.result.nomination.spanEndHour,
                      overridden: shown.result.nomination.overridden,
                    }}
                    /*
                      The record's window, and the scenario envelope carries
                      none. That is correct rather than a gap: the route
                      recomputes under `record.config_version_id` — the run's
                      stamped configuration — so the window a scenario was
                      measured against *is* the record's, and reading it from
                      anywhere else would be a second copy that could disagree.
                    */
                    eventWindow={estimate.eventWindow}
                    variant="internal"
                    /*
                      Red is the *scenario's*. A recompute is drawn in the
                      record's own series colours and carries its state in the
                      band, the page tint and the amber KPI figures instead —
                      lending the scenario's red to a second state would make
                      the two indistinguishable at a glance, which is the exact
                      thing the two treatments exist to prevent.
                    */
                    scenario={isScenario}
                  />
                </section>
                <KpiPair estimate={shown} confidence={estimate.confidence} figures={figures} />
                <LoadDecomposition estimate={shown} confidence={estimate.confidence} />
                {/*
                  Display rule 5, "reachable, not resident". It fetches each aspect
                  when its row is opened, so mounting this screen still costs
                  exactly one request — see `Derivation.tsx`.

                  It is inside the `estimate !== null` branch because a derivation
                  is the working behind a figure, and this run produced no figure
                  for an account it did not score. The route agrees: it answers
                  `estimate_not_found`.

                  `scenarioActive` is Task 16's finding F-4 answered: the four
                  derivations describe the **estimate of record**, and there is
                  no route that can derive a scenario. So opening a what-if
                  closes any open row and drops what it had fetched, and the
                  heading says which estimate the rows are the working for.
                */}
                <Derivation
                  accountId={account.id}
                  confidence={estimate.confidence}
                  confidenceLabelId={CONFIDENCE_ID}
                  scenarioActive={isScenario}
                  asOfActive={asOfActive}
                />
              </>
            )}
          {/*
            Display rule 8 is why this sits *below* the figures rather than
            beside them: a note never influences a displayed estimate, rank or
            confidence label, and nothing on this screen reads one into a
            number. It is inside `drilldown-under-presentation` with everything
            else, which is what keeps R-28's "note content never appears in any
            customer-facing artifact or mode" true of print, `innerText`,
            find-in-page and the accessibility tree and not only of what is on
            screen.
          */}
          {/*
            The strategy surface (F-18, R-32, R-33). It sits below the figures
            and above the notes for the same reason the notes sit below the
            figures: everything on it is advisory, nothing on it moves a
            number, and its position on the page says so before a word is read.

            It is OUTSIDE the `estimate !== null` branch on purpose. An account
            this run did not score still gets a ranked list — every strategy
            visible and unquantified, through the engine's fallback branch —
            because an empty list would tell a rep the account has no options,
            which is a different and false statement. What that account does
            not get is the reject control; see `onSuggestExclusion`.
          */}
          <StrategyList
            accountId={account.id}
            {...(estimate === null || asOfActive ? {} : { onSuggestExclusion })}
          />
          <NoteFeed accountId={account.id} characterization={characterization.state} />
        </div>

        {/*
          **Sticky, for the reason `ScenarioBand` and `AsOfBand` above are.**

          The main column runs to several screens — chart, KPI pair,
          decomposition, four derivations, the strategy list and the note feed —
          and the rail carries the confidence label, the characterization, the
          baseline and the way back. A rep reading a derivation two screens down
          was being asked to remember what the confidence card said, which is
          the same "the page said so at the top" / "the page says so" argument
          spec §7.4 makes about the bands.

          `alignItems: 'start'` on the grid above is what makes this possible: a
          stretched grid item is already as tall as the row and has nothing to
          stick within.

          **`maxHeight` and `overflowY` are not belt and braces.** The rail is
          taller than a 900px viewport with the five cards and the action stack
          on it, and a sticky element taller than its scrollport clips its own
          bottom — the way back would be the part that disappeared.

          It is `100vh` less `CHROME_HEIGHT` and not plain `100vh` because
          `Chrome.tsx` scrolls `<main>` rather than the document: the app bar is
          still on screen when the rail is stuck, so 53px of a `100vh` rail
          would hang below the fold with no scroll able to reach it. The
          scenario bar's 6px are knowingly not subtracted — they apply on one of
          the three states this page has, and a rail 6px too tall under a
          what-if costs the bottom 6px of a 48px padding strip.

          **No `zIndex`.** The two bands carry `zIndex: 20` and both stick at
          `top: 0` as well; leaving this at `auto` is what keeps the scenario
          band painting over the rail rather than under it. A rep must never
          lose "you are looking at a what-if" behind a card.
        */}
        <aside
          data-testid="account-rail"
          style={{
            width: T.rail.account,
            padding: '22px 24px 48px 0',
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
            position: 'sticky',
            top: 0,
            maxHeight: `calc(100vh - ${CHROME_HEIGHT}px)`,
            overflowY: 'auto',
          }}
          aria-label="Account detail"
        >
          {/*
            The confidence card is the **record's**, scenario or not — its label
            and its reasons are the ones that went through the cohort pass, and
            a scenario has neither of its own.
          */}
          <ConfidenceCard estimate={estimate} />
          <CharacterizationCard characterization={characterization} />
          {/*
            Directly under the characterization card, because an approval on
            that card is what produced this figure, and above the baseline and
            program-status cards, because it is news. It renders nothing at all
            on the overwhelming majority of accounts — see `RescoreCard`.
          */}
          <RescoreCard
            ranked={estimate}
            rescored={rescoredSinceRun}
            characterization={rescoredCharacterization}
          />
          <BaselineCard estimate={shown} confidence={estimate?.confidence ?? null} />
          <ProgramStatusCard
            account={account}
            estimate={shown}
            confidence={estimate?.confidence ?? null}
          />
          <ActionStack onBack={onBack} onepager={onepager} />
        </aside>
      </div>
    </>
  )
}

/**
 * Which of the three reasons a figure is on this page. Named rather than
 * boolean because the third one arrived in Task 19 and two booleans would have
 * admitted a fourth, impossible state.
 */
type FigureState = 'record' | 'scenario' | 'asOf'

interface HeaderProps {
  account: DetailAccount
  estimate: AccountEstimate | null
  panelOpen: boolean
  onTogglePanel: () => void
  /** A what-if is on the page, which is what makes presentation mode refuse. */
  scenarioOpen: boolean
  onPresent: () => void
  recompute: Recompute
  onAsOf: (date: string, recordAsOf: string) => void
  onRecordDate: () => void
  /** The one-pager's URL, or `null` when this page must not offer one. */
  onepager: string | null
}

function Header(
  {
    account, estimate, panelOpen, onTogglePanel, scenarioOpen, onPresent,
    recompute, onAsOf, onRecordDate, onepager,
  }: HeaderProps,
): JSX.Element {
  const asOfActive = recompute.phase !== 'none'
  return (
    <header style={{ display: 'flex', justifyContent: 'space-between', gap: 24, alignItems: 'flex-start' }}>
      <div style={{ minWidth: 0 }}>
        <div style={microLabel}>{account.naicsLabel}</div>
        <h1 style={{ fontSize: 29, fontWeight: T.weight.display, letterSpacing: '-0.015em', margin: '6px 0 0' }}>
          {account.name}
        </h1>
        <div style={{ fontSize: 12.5, color: T.color.muted, marginTop: 6 }}>
          {`${account.accountNumber} · ${account.serviceAddress} · Rate ${account.rateClass}`}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }} id={CONFIDENCE_ID}>
          {estimate === null ? null : <ConfidencePill confidence={estimate.confidence} />}
          <StatusPill
            status={account.enrollmentStatus}
            {...(account.nominatedKw === null
              ? {}
              // Sky Harbor's `94` beside `nominated 95 kW`: the current estimate
              // and the nomination of record are two different facts, and the
              // plan is explicit that both are kept and labelled rather than
              // reconciled into one figure.
              : { detail: `nominated ${grouped(account.nominatedKw, 1)} kW` })}
          />
        </div>
        {estimate === null
          ? null
          : (
            <AsOfControl
              recordAsOf={estimate.result.asOfDate}
              recompute={recompute}
              onAsOf={onAsOf}
              onRecordDate={onRecordDate}
              disabledWhy={scenarioOpen ? SCENARIO_HAS_NO_AS_OF : null}
            />
          )}
        <div style={{ display: 'flex', gap: 8 }}>
          {/*
            R-17's entry point. It is a toggle rather than a link because the
            panel is a control over the figures already on this page, and
            `aria-pressed` is what tells a screen reader which of the two states
            it is in — the red fill alone says nothing.
          */}
          {estimate === null
            ? <UnbuiltButton label="What-if" why={NO_ESTIMATE_TO_ASK_ABOUT} />
            : asOfActive
            ? <UnbuiltButton label="What-if" why={AS_OF_HAS_NO_WHATIF} />
            : (
              <button
                type="button"
                onClick={onTogglePanel}
                aria-pressed={panelOpen}
                data-testid="whatif-toggle"
                style={{
                  ...buttonStyle,
                  borderColor: panelOpen ? T.color.red : T.color.border,
                  background: panelOpen ? T.color.red : '#fff',
                  color: panelOpen ? '#fff' : T.color.ink,
                }}
              >
                What-if
              </button>
            )}
          {/*
            R-28's entry point, and the two states in which it refuses are
            different facts said differently: there is no estimate to present,
            or there is a what-if on the page and a what-if has no customer
            payload. Neither says "not built yet"; both are built.
          */}
          {estimate === null
            ? <UnbuiltButton label="Presentation view" why={NO_ESTIMATE_TO_PRESENT} />
            : scenarioOpen
              ? <UnbuiltButton label="Presentation view" why={SCENARIO_HAS_NO_PRESENTATION} />
              : asOfActive
                ? <UnbuiltButton label="Presentation view" why={AS_OF_HAS_NO_PRESENTATION} />
                : (
                <button
                  type="button"
                  onClick={onPresent}
                  data-testid="present"
                  style={buttonStyle}
                >
                  Presentation view
                </button>
              )}
          {/*
            The one control here that works today. `GET /api/accounts/:id/onepager`
            is a real route (Task 7) serving a print-styled HTML document, and it
            is a link rather than a button because opening a document in a new
            tab is what a link does — including on a middle click, which no
            handler here has to reimplement.
          */}
          {onepager === null
            ? <UnbuiltButton label="One-pager" why={AS_OF_HAS_NO_ONEPAGER} />
            : (
              <a
                href={onepager}
                target="_blank"
                rel="noreferrer"
                data-testid="one-pager"
                style={{ ...buttonStyle, borderColor: T.color.navy, color: T.color.navy, textDecoration: 'none' }}
              >
                One-pager
              </a>
            )}
        </div>
      </div>
    </header>
  )
}

/**
 * The date this page is scored for — a real `<input type="date">` in the
 * account header, defaulting to the run's own as-of date.
 *
 * **Its bounds are the program's, and this package does not know them.** The
 * season lives in the stamped configuration document; writing `2026-06-01` and
 * `2026-10-15` into a browser bundle would be program configuration versioned
 * by nobody, which is the mistake the mock's client-side confidence weights
 * already are. `api.config()` — `GET /api/config/current`, `requireAuth` and
 * rep-readable — answers with the season, so the bounds come from the program.
 *
 * They are fetched **on mount**, and the reason that is now allowed is the
 * reason this route was built. It used to be first focus: the bounds came from
 * the `config` **derivation**, display rule 5 says a derivation is fetched on
 * open rather than on mount, and `AccountDetail.test.tsx` asserts that opening
 * an account issues no derivation request at all — so populating two attributes
 * eagerly would have traded a shipped property for a convenience. A
 * configuration read is not a derivation request, so that trade no longer
 * exists and the control is correct before a rep touches it.
 *
 * The request does not depend on the account, so it is not re-issued when the
 * page moves to a different one: what is in force is program-wide, and asking
 * again per account would be one request per navigation for an answer that
 * cannot have changed.
 *
 * And they are an assist rather than the check. The **server** is what refuses
 * an out-of-season date, and its refusal names the season out of the same
 * document the bounds came from — so a date that slips past an unpopulated
 * `min`/`max` is answered correctly, in the program's own words, on the band.
 */
function AsOfControl(
  { recordAsOf, recompute, onAsOf, onRecordDate, disabledWhy }: {
    recordAsOf: string
    recompute: Recompute
    onAsOf: (date: string, recordAsOf: string) => void
    onRecordDate: () => void
    disabledWhy: string | null
  },
): JSX.Element {
  const [season, setSeason] = useState<{ start: string; end: string } | null>(null)

  useEffect(() => {
    let cancelled = false
    void api.config()
      .then((config) => { if (!cancelled) setSeason(config.season) })
      // Silent, and deliberately: the bounds are an assist and the server is
      // the check. A rep does not need to be told that an attribute they
      // cannot see did not arrive.
      .catch(() => undefined)
    return () => { cancelled = true }
  }, [])

  const value = recompute.phase === 'none' ? recordAsOf : recompute.asOf
  const disabled = disabledWhy !== null

  return (
    <span
      title={disabledWhy ?? undefined}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
    >
      <label htmlFor="account-as-of" style={{ ...microLabel, color: T.color.micro }}>
        {AS_OF_LABEL}
      </label>
      <input
        id="account-as-of"
        type="date"
        data-testid="as-of-input"
        value={value}
        disabled={disabled}
        title={disabledWhy ?? undefined}
        {...(season === null ? {} : { min: season.start, max: season.end })}
        onChange={(event) => {
          const next = event.currentTarget.value
          // A half-typed date sanitizes to the empty string rather than to a
          // day, and asking the server to score "" is not a question.
          if (next === '') return
          if (next === recordAsOf) { onRecordDate(); return }
          onAsOf(next, recordAsOf)
        }}
        style={{
          fontFamily: T.fontFamily,
          fontSize: 12.5,
          padding: '7px 9px',
          border: `1.5px solid ${T.color.border}`,
          borderRadius: T.radius.square,
          background: disabled ? T.tint.rescore : '#fff',
          color: disabled ? T.color.micro : T.color.ink,
          cursor: disabled ? 'not-allowed' : 'text',
        }}
      />
    </span>
  )
}

const buttonStyle: CSSProperties = {
  border: `1.5px solid ${T.color.border}`,
  background: '#fff',
  color: T.color.ink,
  padding: '9px 13px',
  fontFamily: T.fontFamily,
  fontSize: T.micro.size,
  fontWeight: T.weight.bold,
  letterSpacing: '0.08em',
  textTransform: T.micro.transform,
  borderRadius: T.radius.button,
  cursor: 'pointer',
  display: 'inline-block',
}

/**
 * A control that is not built, drawn as a control that is not built.
 *
 * The `title` sits on the wrapping `<span>` as well as on the button for the
 * reason `Chrome.tsx` documents: browsers do not dispatch mouse events to a
 * disabled form control, so a `title` on the button alone never opens a tooltip
 * — the one place the explanation is actually read. The button keeps its own
 * copy because that is what reaches the accessibility tree.
 */
function UnbuiltButton({ label, why }: { label: string; why: string }): JSX.Element {
  return (
    <span title={why} style={{ display: 'inline-flex' }}>
      <button
        type="button"
        disabled
        title={why}
        style={{ ...buttonStyle, color: T.color.micro, cursor: 'not-allowed' }}
      >
        {label}
      </button>
    </span>
  )
}

/**
 * The run did not score this account, so there are no figures — not zeroed
 * figures, not a spinner that never resolves.
 *
 * The account's own eligibility reason is the answer to "why", and it is the
 * server's words rather than this screen's guess.
 */
function NotScored({ account }: { account: DetailAccount }): JSX.Element {
  return (
    <section style={sectionRule} data-testid="not-scored">
      <div style={microLabel}>No estimate on this run</div>
      <p style={{ fontSize: 14.5, lineHeight: 1.55, color: T.color.body, marginTop: 8 }}>
        {account.eligibilityStatus === 'out_of_scope'
          ? `This account is out of scope for the program, so the run did not score it: ${account.eligibilityReason}`
          : 'The most recent completed run has no estimate for this account.'}
      </p>
    </section>
  )
}

/**
 * Every flag, one list item each.
 *
 * The mock renders `flags[0]`. Two flags showing as one is a rep making a
 * commitment against information the system had — and the flag that gets
 * dropped is as likely to be `cohort_outlier` ("verify before nominating") as
 * the harmless one.
 *
 * `basis` says which estimate the list came from — see the call site — and is
 * on the markup as well as in the note, because "whose flags are these" is a
 * question about the figures beside them and not a caption.
 */
type FlagBasis = 'record' | 'record-under-scenario' | 'recomputed'

function InternalFlags(
  { flags, basis }: { flags: EstimateFlag[]; basis: FlagBasis },
): JSX.Element | null {
  if (flags.length === 0) return null
  return (
    <section
      data-testid="internal-flags"
      data-flags-basis={basis}
      style={{ ...sectionRule, background: T.tint.red, border: `1px solid ${T.color.red}`, padding: 16, marginTop: 20, borderTopWidth: 1 }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <span
          style={{
            ...microLabel,
            color: '#fff',
            background: T.color.red,
            borderRadius: T.radius.pill,
            padding: '3px 10px',
            fontWeight: T.weight.bold,
          }}
        >
          Internal
        </span>
        <span style={{ fontSize: 12, color: T.color.declinedInk, fontWeight: T.weight.semi }}>
          {flags.length === 1 ? '1 flag' : `${flags.length} flags`}
        </span>
      </div>
      <ul style={{ margin: '10px 0 0', paddingLeft: 18, fontSize: 13.5, color: T.color.ink }}>
        {flags.map((flag) => (
          <li key={flag.code} data-flag-code={flag.code} style={{ marginTop: 4 }}>
            {flag.message}
          </li>
        ))}
      </ul>
      <div style={{ fontSize: 11.5, color: T.color.micro, marginTop: 10 }}>
        {basis === 'record-under-scenario'
          ? `${FLAGS_OF_RECORD_NOTE} ${FLAGS_HIDDEN_NOTE}`
          : FLAGS_HIDDEN_NOTE}
      </div>
    </section>
  )
}

/**
 * The two figures a rep actually quotes, side by side.
 *
 * The season value is **split**. R-11 asks for capacity and energy separately
 * and the total alone would hide which half a change moves: a shorter event
 * changes the energy payment and leaves the capacity payment where it was.
 */
const KPI_TONE = { record: 'navy', scenario: 'red', asOf: 'warn' } as const
const KPI_MONEY = { record: T.color.money, scenario: T.color.red, asOf: T.color.warnInk } as const

function KpiPair(
  { estimate, confidence, figures }:
  { estimate: EstimateFigures; confidence: ConfidenceLabel; figures: FigureState },
): JSX.Element {
  const { nomination, value, profile } = estimate.result
  // Counted off the profile rather than from `spanEndHour - spanStartHour`.
  // The two agree on every account this book produces — the span is contiguous
  // and every hour in it was metered — so this is not a bug fix, it is the
  // difference between counting the readings the caption claims and counting
  // the hours on a clock. A profile with a gap in it (a meter exchange, an
  // unmeasured hour) would make the arithmetic version report a reading that
  // was never taken, in a sentence whose whole job is to say how much evidence
  // is behind the number.
  const sustained = profile.filter(
    (hour) => hour.hour >= nomination.spanStartHour && hour.hour < nomination.spanEndHour,
  ).length
  const readings = `${grouped(sustained, 0)} hourly ${sustained === 1 ? 'reading' : 'readings'}`

  return (
    <section style={{ ...sectionRule, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      <div data-testid="kpi-nomination" style={{ background: T.tint.blue, padding: '16px 18px' }}>
        <div style={microLabel}>Recommended nomination</div>
        {/*
          The one figure on this screen that carries its own pill rather than
          naming the header's, and it is the only one that needs to.

          Found by looking at the rendered page: the 44px headline sits ~500px
          from the header pill, and on a Medium account — where the label is
          doing the most work it ever does, "held one level below what the data
          quality alone would support" — a rep reading the number aloud has no
          label in view. Display rule 1 admits no bare kW figure, and "the label
          is up and to the right, off the top of this panel" is the reading of
          that rule which the rule exists to refuse. The plan's KPI spec is
          otherwise unchanged: 44px, navy, on the blue tint.
        */}
        <div style={{ marginTop: 6 }}>
          <KwFigure
            kw={nomination.kw}
            confidence={confidence}
            size="kpi"
            // Display rule 2 at the one figure a rep is most likely to read
            // aloud: under a what-if this is a scenario's number and is drawn
            // in the scenario red, not in the record's navy — and under a
            // recompute it is a third day's number and a third colour.
            tone={KPI_TONE[figures]}
            label="pill"
            testId="nomination-kw"
          />
        </div>
        <div data-testid="nomination-caption" style={{ fontSize: 12, color: T.color.muted, marginTop: 8 }}>
          {/*
            "Binds at" is a claim about where the figure came from, and a
            hand-entered nomination did not come from any hour. Task 14 withheld
            `BINDS` from the chart for the same reason and left this half open
            as its finding F-5; this is that half. The weakest hour is still
            reported, as an observation rather than as a cause.
          */}
          {nomination.overridden
            ? `Entered by hand · the weakest of ${readings} is ${formatHour(nomination.bindingHour)}`
            : `Binds at ${formatHour(nomination.bindingHour)} · sustained across ${readings}`}
        </div>
      </div>

      <div data-testid="kpi-season-value" style={{ background: T.tint.green, padding: '16px 18px' }}>
        <div style={microLabel}>Estimated season value</div>
        <div
          data-testid="season-total"
          style={{
            marginTop: 6,
            fontSize: 44,
            fontWeight: T.weight.display,
            color: KPI_MONEY[figures],
            fontVariantNumeric: 'tabular-nums',
          }}
          data-scenario={figures === 'scenario' ? 'true' : 'false'}
          data-figures={figures}
        >
          {formatMoney(value.seasonTotal)}
        </div>
        <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', margin: '10px 0 0', fontSize: 12.5 }}>
          <dt style={{ color: T.color.muted }}>Capacity</dt>
          <dd data-testid="season-capacity" style={{ margin: 0, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
            {formatMoney(value.seasonDemand)}
          </dd>
          <dt style={{ color: T.color.muted }}>Energy</dt>
          <dd data-testid="season-energy" style={{ margin: 0, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
            {formatMoney(value.seasonEnergy)}
          </dd>
        </dl>
      </div>
    </section>
  )
}

/**
 * The three fitted components, with their real units.
 *
 * `coolingKwPerDegreeHour` is **not** a kW figure and is not drawn as one: it
 * is a slope, kW per degree-hour, and printing it as "2.5 kW" beside a base
 * load of 100 kW would invite a rep to add them. The two that *are* loads go
 * through `KwFigure` like every other kW quantity on this screen.
 *
 * `null` on the fallback path, where no decomposition was performed — 134
 * accounts on this book. That is a method, not a defect (display rule 4), so it
 * says which method produced the figure rather than showing an empty table.
 */
function LoadDecomposition(
  { estimate, confidence }: { estimate: EstimateFigures; confidence: ConfidenceLabel },
): JSX.Element {
  const { decomposition } = estimate.result
  return (
    <section style={sectionRule} data-testid="decomposition">
      <div style={microLabel}>Load decomposition</div>
      {decomposition === null
        ? (
          <p style={{ fontSize: 14.5, lineHeight: 1.55, color: T.color.body, marginTop: 8 }}>
            This estimate came from the fallback path: the account&rsquo;s meter history did not
            support a decomposition, so no components were fitted. The nomination above still
            stands — its confidence label already carries the difference.
          </p>
        )
        : (
          <>
            <dl style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, margin: '12px 0 0' }}>
              <Component label="Base load" testId="component-base">
                <KwFigure
                  kw={decomposition.baseKw}
                  confidence={confidence}
                  label="adjacent"
                  confidenceLabelId={CONFIDENCE_ID}
                  testId="base-kw"
                />
              </Component>
              <Component label="Cooling" testId="component-cooling">
                <span
                  data-testid="cooling-slope"
                  style={{ fontVariantNumeric: 'tabular-nums', fontWeight: T.weight.semi }}
                >
                  {`${grouped(decomposition.coolingKwPerDegreeHour, 2)} kW per °F-hour`}
                </span>
              </Component>
              <Component label="Schedule lift" testId="component-schedule">
                <KwFigure
                  kw={decomposition.scheduleLiftKw}
                  confidence={confidence}
                  label="adjacent"
                  confidenceLabelId={CONFIDENCE_ID}
                  testId="schedule-kw"
                />
              </Component>
            </dl>
            <div data-testid="fit-diagnostics" style={{ fontSize: 11.5, color: T.color.micro, marginTop: 12 }}>
              {`Fit R² ${grouped(decomposition.fitR2, 2)} · mean reconciliation error `
                + `${grouped(decomposition.reconciliationErrorKw, 1)} kW. Internal fit diagnostics; `
                + 'neither appears in presentation view or on the one-pager.'}
            </div>
          </>
        )}
    </section>
  )
}

function Component({ label, testId, children }: { label: string; testId: string; children: ReactNode }): JSX.Element {
  return (
    <div data-testid={testId} style={{ borderTop: `1px solid ${T.color.hairline}`, paddingTop: 10 }}>
      <dt style={{ ...microLabel, marginBottom: 6 }}>{label}</dt>
      <dd style={{ margin: 0, fontSize: 16 }}>{children}</dd>
    </div>
  )
}

interface RailCardProps {
  title: string
  rail: string
  testId: string
  children: ReactNode
}

/**
 * A right-rail card with a 4px inset colour rail down its left edge.
 *
 * The rail colour is also a `data-rail-colour` attribute, and the tests read
 * that rather than a computed style: jsdom answers most computed style queries
 * with `''` or a default, and this campaign has three separate cases of a test
 * that passed against gutted code because it was measuring the environment.
 */
function RailCard({ title, rail, testId, children }: RailCardProps): JSX.Element {
  return (
    <section
      data-testid={testId}
      data-rail-colour={rail}
      style={{
        background: '#fff',
        border: `1px solid ${T.color.hairline}`,
        borderLeft: `4px solid ${rail}`,
        padding: '14px 16px',
      }}
    >
      <h2 style={{ ...microLabel, margin: 0 }}>{title}</h2>
      <div style={{ marginTop: 8 }}>{children}</div>
    </section>
  )
}

/**
 * The rail colour of the confidence card is the confidence itself.
 *
 * Low is the quiet one — the same argument `Pill.tsx` makes about the Low pill
 * being white and dashed rather than red. A Low-confidence estimate is still an
 * estimate worth calling on; it is an instruction about how to talk about the
 * number, not an alarm.
 */
const CONFIDENCE_RAIL: Record<ConfidenceLabel, string> = {
  high: T.color.green,
  medium: T.color.amber,
  low: T.color.dashedBorder,
}

function ConfidenceCard({ estimate }: { estimate: AccountEstimate | null }): JSX.Element {
  const reasons = estimate?.result.confidence.reasons ?? []
  return (
    <RailCard
      title="Confidence"
      rail={estimate === null ? T.color.chipOff : CONFIDENCE_RAIL[estimate.confidence]}
      testId="confidence-card"
    >
      {estimate === null
        ? <p style={{ margin: 0, fontSize: 13, color: T.color.micro }}>No estimate on this run.</p>
        : (
          <>
            <ConfidencePill confidence={estimate.confidence} />
            {reasons.length === 0
              ? null
              : (
                <ul data-testid="confidence-reasons" style={{ margin: '10px 0 0', paddingLeft: 16, fontSize: 12.5, color: T.color.body }}>
                  {reasons.map((reason) => <li key={reason} style={{ marginTop: 4 }}>{reason}</li>)}
                </ul>
              )}
          </>
        )}
    </RailCard>
  )
}

/**
 * The server sends three states; `Pill.tsx` draws three treatments. This is the
 * one-line adapter between them, and it exists so the two internal states
 * cannot collapse into each other on the way across (display rule 3).
 */
function toPillCharacterization(characterization: AccountCharacterization): Characterization {
  if (characterization.state === 'recorded' && characterization.buildingType !== null) {
    return { state: 'recorded', buildingType: characterization.buildingType }
  }
  if (characterization.state === 'undetermined') return { state: 'undetermined', buildingType: null }
  return { state: 'none', buildingType: null }
}

function CharacterizationCard({ characterization }: { characterization: AccountCharacterization }): JSX.Element {
  const { provenance } = characterization
  return (
    <RailCard title="Building characterization" rail={T.color.cyan} testId="characterization-card">
      <div style={{ fontSize: 13.5 }}>
        <CharacterizationLabel {...toPillCharacterization(characterization)} />
      </div>
      {/*
        Display rule 6 — who, when, from what source — and display rule 4 in the
        same breath: an uncharacterized account has no human entry to attribute,
        so it gets no provenance block and no prompt to create one. The absence
        is the normal case, not an empty slot.
      */}
      {provenance === null
        ? null
        : (
          <dl
            data-testid="provenance"
            style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '3px 10px', margin: '10px 0 0', fontSize: 11.5 }}
          >
            <dt style={{ color: T.color.micro }}>Entered by</dt>
            <dd style={{ margin: 0 }}>{provenance.enteredBy}</dd>
            <dt style={{ color: T.color.micro }}>When</dt>
            <dd style={{ margin: 0 }}>{formatDate(provenance.enteredAt)}</dd>
            <dt style={{ color: T.color.micro }}>Source</dt>
            <dd style={{ margin: 0 }}>{provenance.researchSource}</dd>
            {characterization.version === null
              ? null
              : (
                <>
                  <dt style={{ color: T.color.micro }}>Revision</dt>
                  <dd style={{ margin: 0 }} data-testid="characterization-version">
                    {grouped(characterization.version, 0)}
                  </dd>
                </>
              )}
          </dl>
        )}
    </RailCard>
  )
}

/**
 * R-25's second figure: the estimate an analyst's approval re-scored this
 * account to, since the run the targeting list ranked it from.
 *
 * ## Which figure is which, and why this one is not the headline
 *
 * The KPI band, the header pill, the profile chart, the one-pager and this
 * account's rank on the targeting list are all the **ranked** estimate — the
 * one the latest completed run wrote. That figure is not the freshest; it is
 * the *comparable* one. `queryAccounts` ranks the whole book against itself
 * from one run's numbers, and a list in which some rows carried an approval's
 * figure and the rest carried the run's would not be a ranking of anything, so
 * the list stays on the run and Task 6 settled that deliberately.
 *
 * This card is therefore the only place the re-score appears, it appears
 * *beside* the ranked figure rather than over it, and it says in words which is
 * which. Nothing on this page is recomputed from it and nothing is reconciled:
 * two figures both labelled is the same ruling `ProgramStatusCard` already
 * makes about a contractual nomination beside a computed estimate.
 *
 * ## It draws its own confidence pill
 *
 * `label="pill"` rather than `label="adjacent"`, which is the choice that would
 * have been wrong quietly. `CONFIDENCE_ID` names the header pill, and that pill
 * is the **ranked** estimate's label, page-wide and on purpose. A re-score
 * against a newly approved building type is exactly the kind of thing that
 * moves a confidence band — `characterizationUpliftEnabled` is a configured
 * input to `scoreConfidence` — so pointing this figure at the header pill would
 * put display rule 1's label beside a number it is not the label for.
 *
 * ## The configuration-version line
 *
 * `characterization/review.ts` re-scores against the ranked run's own
 * `config_version_id`, so the two estimates are normally computed under one
 * document and the whole difference between them is the approval. That is a
 * property of one call site rather than of the payload, and this card is the
 * place it becomes visible: when the stamps differ, the difference is not only
 * the approval and the card says so instead of letting a rep attribute it.
 */
const RESCORE_NOTE
  = 'An analyst approved a characterization for this account after the run that ranked it, and '
  + 'this is what the account scores against that approval. Every other figure on this page — and '
  + 'this account’s place on the targeting list — is still the run’s, until the next run absorbs '
  + 'the approval.'

/**
 * Display rule 3's middle state, said in words, on the only card that can say
 * it before the next run.
 *
 * `GET /api/accounts/:id` pins `characterization` to the **ranked** estimate's
 * revision — Plan 3's Critical C-1, and right: the card beside the ranked
 * figure must describe what that figure was computed from, and an approval
 * landing after the run must not silently rewrite it. What it costs, measured:
 * an `undetermined` approved this morning reads `state: 'uncharacterized'` on
 * the detail route and on `/derivation` until the next run absorbs it. That is
 * *nobody has looked* printed for an account somebody looked at and could not
 * identify — the collapse rule 3 exists to prevent, and the one that decides
 * whether anyone is sent to look at it again.
 *
 * The pin is not what changes. The re-scored figure carries its own
 * characterization now, so each card describes the estimate it sits under, and
 * this sentence is drawn for the middle state alone: a rep reading it needs to
 * know both that the account was researched and that it still has no type.
 */
const RESCORE_UNDETERMINED
  = 'Somebody researched this account and could not identify the building type. That is a finding, '
  + 'not a gap — it is not waiting on anyone. The card above still reads “not characterized” '
  + 'because it describes the ranked figure, which was computed before this approval.'

/** Shown only when the two estimates were not computed under one configuration version. */
const RESCORE_CONFIG_DIVERGED
  = 'These two figures were computed under different configuration versions, so the difference '
  + 'between them is not only the approval.'

function RescoreCard(
  { ranked, rescored, characterization }: {
    ranked: AccountEstimate | null
    rescored: AccountEstimate | null
    characterization: AccountCharacterization | null
  },
): JSX.Element | null {
  // The ordinary answer on 313 accounts out of 313 for a book nobody has
  // reviewed today. A card that rendered an empty state here would put a
  // permanent "nothing has happened" slot in the rail of every account.
  if (rescored === null) return null
  const diverged = ranked !== null && ranked.configVersionId !== rescored.configVersionId
  return (
    <RailCard title="Re-scored since this run" rail={T.color.amber} testId="rescore-card">
      <KwFigure
        kw={rescored.curtailableKwRaw}
        confidence={rescored.confidence}
        size="hero"
        tone="warn"
        label="pill"
        testId="rescore-kw"
      />
      <dl
        data-testid="rescore-provenance"
        style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '3px 10px', margin: '10px 0 0', fontSize: 11.5 }}
      >
        <dt style={{ color: T.color.micro }}>Ranked figure</dt>
        <dd style={{ margin: 0 }} data-testid="rescore-ranked-kw">
          {ranked === null ? 'None — this account was not scored by that run.' : `${grouped(ranked.curtailableKwRaw, 1)} kW`}
        </dd>
        {rescored.characterizationVersionId === null
          ? null
          : (
            <>
              <dt style={{ color: T.color.micro }}>Approved revision</dt>
              <dd style={{ margin: 0 }} data-testid="rescore-characterization-version">
                {rescored.characterizationVersionId}
              </dd>
            </>
          )}
        {/*
          Display rule 3's three states, on the one card that can tell them
          apart between an approval and the next run. See RESCORE_STATE below —
          a revision number does not distinguish *undetermined* from *office*,
          and the card above this one says `uncharacterized` for both.
        */}
        {characterization === null
          ? null
          : (
            <>
              <dt style={{ color: T.color.micro }}>Approved as</dt>
              <dd style={{ margin: 0 }} data-testid="rescore-characterization-state">
                <CharacterizationLabel {...toPillCharacterization(characterization)} />
              </dd>
            </>
          )}
      </dl>
      {characterization?.state === 'undetermined'
        ? (
          <p
            data-testid="rescore-undetermined"
            style={{ fontSize: 11.5, color: T.color.warnInk, margin: '8px 0 0', lineHeight: 1.5 }}
          >
            {RESCORE_UNDETERMINED}
          </p>
        )
        : null}
      <p style={{ fontSize: 11.5, color: T.color.warnInk, margin: '8px 0 0', lineHeight: 1.5 }}>
        {RESCORE_NOTE}
      </p>
      {diverged
        ? (
          <p
            data-testid="rescore-config-diverged"
            style={{ fontSize: 11.5, color: T.color.warnInk, margin: '6px 0 0', lineHeight: 1.5 }}
          >
            {RESCORE_CONFIG_DIVERGED}
          </p>
        )
        : null}
    </RailCard>
  )
}

function BaselineCard(
  { estimate, confidence }: { estimate: EstimateFigures | null; confidence: ConfidenceLabel | null },
): JSX.Element {
  return (
    <RailCard title="Baseline" rail={T.color.lime} testId="baseline-card">
      {estimate === null || confidence === null || estimate.baselinePeakKw === null
        ? (
          // R-2: nothing fabricates a baseline. An account with no selectable
          // comparable days has no baseline, and saying so is the honest answer.
          <p style={{ margin: 0, fontSize: 13, color: T.color.micro }}>No baseline could be selected.</p>
        )
        : (
          <>
            <div style={{ fontSize: 24 }}>
              <KwFigure
                kw={estimate.baselinePeakKw}
                confidence={confidence}
                label="adjacent"
                confidenceLabelId={CONFIDENCE_ID}
                testId="baseline-kw"
              />
            </div>
            <div style={{ fontSize: 11.5, color: T.color.micro, marginTop: 6 }}>{BASELINE_CAPTION}</div>
          </>
        )}
    </RailCard>
  )
}

/**
 * Enrollment, the nomination of record, and what the run thinks of the account.
 *
 * The nomination of record is a *contractual* figure and the curtailable
 * estimate is a computed one; the mock shows Sky Harbor's 94 beside "nominated
 * 95 kW" and never explains it. Both are kept and labelled here rather than
 * reconciled, because reconciling them either overwrites a contract with an
 * estimate or makes the estimate look wrong.
 */
function ProgramStatusCard(
  { account, estimate, confidence }:
  { account: DetailAccount; estimate: EstimateFigures | null; confidence: ConfidenceLabel | null },
): JSX.Element {
  return (
    <RailCard title="Program status" rail={T.color.navy} testId="status-card">
      <StatusPill status={account.enrollmentStatus} />
      <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 10px', margin: '10px 0 0', fontSize: 12 }}>
        {account.nominatedKw === null
          ? null
          : (
            <>
              <dt style={{ color: T.color.micro }}>Nomination of record</dt>
              <dd style={{ margin: 0, textAlign: 'right' }} data-testid="nomination-of-record">
                {`${grouped(account.nominatedKw, 1)} kW`}
              </dd>
            </>
          )}
        {estimate === null || confidence === null
          ? null
          : (
            <>
              <dt style={{ color: T.color.micro }}>Current estimate</dt>
              <dd style={{ margin: 0, textAlign: 'right' }}>
                <KwFigure
                  kw={estimate.curtailableKwRaw}
                  confidence={confidence}
                  label="adjacent"
                  confidenceLabelId={CONFIDENCE_ID}
                  testId="status-current-kw"
                />
              </dd>
            </>
          )}
        <dt style={{ color: T.color.micro }}>Eligibility</dt>
        <dd style={{ margin: 0, textAlign: 'right' }}>
          {account.eligibilityStatus === 'in_scope' ? 'In scope' : 'Out of scope'}
        </dd>
        <dt style={{ color: T.color.micro }}>On-site generation</dt>
        <dd style={{ margin: 0, textAlign: 'right' }}>
          {account.derStatus === 'known_pv'
            ? `PV ${account.pvNameplateKw === null ? 'on site' : `${grouped(account.pvNameplateKw, 0)} kW`}`
            : account.derStatus === 'confirmed_none' ? 'None confirmed' : 'Unknown'}
        </dd>
      </dl>
    </RailCard>
  )
}

/**
 * What a rep can do from here.
 *
 * `Record an outcome` is the one that is still unbuilt, and it says so rather
 * than being left off: a control that leads nowhere silently is
 * indistinguishable from a broken one. `Back to targeting` is a real control
 * rather than a reliance on the browser's back button, which after a deep link
 * goes somewhere else entirely.
 */
function ActionStack(
  { onBack, onepager }: { onBack: () => void; onepager: string | null },
): JSX.Element {
  return (
    <RailCard title="Actions" rail={T.color.chipOff} testId="action-stack">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'stretch' }}>
        <button
          type="button"
          onClick={onBack}
          style={{ ...buttonStyle, textAlign: 'center' }}
          data-testid="back-to-targeting"
        >
          Back to targeting
        </button>
        {/*
          The second link to the same document, and it carries the same
          scenario. A one-pager link that dropped the overrides here would be
          the F-3 hole reopened one card lower down the page — which is exactly
          how a fix like this comes undone.
        */}
        {onepager === null
          ? <UnbuiltButton label="Open the one-pager" why={AS_OF_HAS_NO_ONEPAGER} />
          : (
            <a
              href={onepager}
              target="_blank"
              rel="noreferrer"
              data-testid="one-pager-action"
              style={{ ...buttonStyle, textAlign: 'center', textDecoration: 'none' }}
            >
              Open the one-pager
            </a>
          )}
        {/*
          `Add a note` used to sit here, disabled, saying "note capture arrives
          in a later release". It is gone rather than re-pointed: capture is on
          this page now, in the composer at the foot of the main column, and a
          disabled button promising a feature that is six inches below it is a
          worse lie than the honest "not built" it replaced.
        */}
        <UnbuiltButton label="Record an outcome" why={NOT_BUILT_OUTCOMES} />
      </div>
    </RailCard>
  )
}
